import { BadgeDollarSign, Gift, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { IntegrationDraftOperation, IntegrationDraftRate } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

import { copyFor, formatRate, perCallCost, rateIdFromLabel, type StepProps } from '../builderModel';
import { Field, StepFrame } from '../builderUi';

type RateRow = IntegrationDraftRate & { label: string; isNew?: boolean };

const BILLING_MODELS: Array<{ id: IntegrationDraftRate['billingModel']; label: string }> = [
  { id: 'metered', label: 'Metered' },
  { id: 'per_action', label: 'Per action' },
  { id: 'recurring', label: 'Recurring' },
  { id: 'included', label: 'Included' },
];

const INTERVALS: Array<{ id: IntegrationDraftRate['interval']; label: string }> = [
  { id: 'request', label: 'Per request' },
  { id: 'segment', label: 'Per segment' },
  { id: 'minute', label: 'Per minute' },
  { id: 'hour', label: 'Per hour' },
  { id: 'day', label: 'Per day' },
  { id: 'month', label: 'Per month' },
  { id: 'year', label: 'Per year' },
];

const FREE = '__free__';

function toRows(props: StepProps): RateRow[] {
  const labels = copyFor(props.draft, 'en').rateLabels;
  return props.draft.pricing.map((rate) => ({ ...rate, unitsPerPrice: rate.unitsPerPrice || 1, label: labels[rate.id] ?? '' }));
}

function toRate(row: RateRow): IntegrationDraftRate {
  return {
    id: row.id,
    providerRate: row.billingModel === 'included' ? 0 : row.providerRate,
    unit: row.unit.trim(),
    billingModel: row.billingModel,
    interval: row.interval,
    unitsPerPrice: Math.max(1, Math.floor(row.unitsPerPrice || 1)),
    ...(row.freeTier ? { freeTier: row.freeTier } : {}),
  };
}

function enabledOperations(props: StepProps): IntegrationDraftOperation[] {
  return props.draft.contract?.operations.filter((operation) => operation.enabled) ?? [];
}

function linksOf(props: StepProps): Record<string, string> {
  return Object.fromEntries(enabledOperations(props).map((operation) => [operation.operationId, operation.rateId ?? FREE]));
}

export function PricingStep(props: StepProps) {
  const { draft, readOnly, saving, save, next } = props;
  const [rows, setRows] = useState<RateRow[]>(() => toRows(props));
  const [links, setLinks] = useState<Record<string, string>>(() => linksOf(props));
  const operations = enabledOperations(props);
  const free = rows.length === 0;

  const comparable = (list: RateRow[], map: Record<string, string>) => JSON.stringify([list.map((row) => [toRate(row), row.label]), map]);
  const dirty = comparable(rows, links) !== comparable(toRows(props), linksOf(props));

  const setRow = (index: number, patch: Partial<RateRow>) => {
    const before = rows[index];
    setRows((current) =>
      current.map((row, i) => {
        if (i !== index) return row;
        const nextRow = { ...row, ...patch };
        if (row.isNew && patch.label !== undefined) {
          nextRow.id = rateIdFromLabel(patch.label, current.filter((_, j) => j !== index).map((item) => item.id));
        }
        return nextRow;
      }),
    );
    // A new rate's id follows its label; keep operations linked to it.
    if (before?.isNew && patch.label !== undefined) {
      const nextId = rateIdFromLabel(patch.label, rows.filter((_, j) => j !== index).map((item) => item.id));
      setLinks((current) => Object.fromEntries(Object.entries(current).map(([op, rateId]) => [op, rateId === before.id ? nextId : rateId])));
    }
  };

  const addRow = () => {
    const id = rateIdFromLabel('calls', rows.map((row) => row.id));
    setRows((current) => [
      ...current,
      { id, label: '', providerRate: 0, unit: 'per 1,000 calls', unitsPerPrice: 1000, billingModel: 'metered', interval: 'request', isNew: true },
    ]);
    // The first rate starts with every operation billed against it; adjust below.
    if (rows.length === 0) setLinks(Object.fromEntries(operations.map((operation) => [operation.operationId, id])));
  };

  const removeRow = (index: number) => {
    const removed = rows[index];
    setRows((current) => current.filter((_, i) => i !== index));
    setLinks((current) => Object.fromEntries(Object.entries(current).map(([op, rateId]) => [op, rateId === removed.id ? FREE : rateId])));
  };

  const persist = () => {
    const en = copyFor(draft, 'en');
    const rateIds = new Set(rows.map((row) => row.id));
    return save({
      pricing: rows.map(toRate),
      copy: { en: { ...en, rateLabels: Object.fromEntries(rows.map((row) => [row.id, row.label.trim()])) } },
      operations: operations.map((operation) => {
        const rateId = links[operation.operationId];
        return {
          operationId: operation.operationId,
          kind: operation.kind,
          enabled: operation.enabled,
          ...(rateId && rateId !== FREE && rateIds.has(rateId) ? { rateId } : {}),
        };
      }),
    });
  };

  return (
    <StepFrame
      title="Pricing"
      description="Optional. With no rates the integration is free — calls are still recorded, so usage stays visible. When the service costs money, enter the provider's USD list price; Studio adds each organization's markup and bills every call through the usage ledger."
      dirty={dirty}
      saving={saving}
      readOnly={readOnly}
      onSave={() => void persist()}
      onContinue={async () => {
        if (!dirty || (await persist())) next();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          disabled={readOnly || free}
          onClick={() => {
            setRows([]);
            setLinks(Object.fromEntries(operations.map((operation) => [operation.operationId, FREE])));
          }}
          className={cn('flex gap-3 rounded-xl border p-4 text-left transition-colors', free ? 'border-brand bg-brand-muted/40' : 'border-border hover:bg-muted/30')}
        >
          <Gift className={cn('mt-0.5 h-4 w-4 shrink-0', free ? 'text-brand' : 'text-muted-foreground')} />
          <span>
            <span className="block text-sm font-medium">Free</span>
            <span className="mt-1 block text-xs leading-5 text-muted-foreground">No charge to organizations. Every call is still recorded at $0.</span>
          </span>
        </button>
        <button
          type="button"
          disabled={readOnly || !free}
          onClick={addRow}
          className={cn('flex gap-3 rounded-xl border p-4 text-left transition-colors', !free ? 'border-brand bg-brand-muted/40' : 'border-border hover:bg-muted/30')}
        >
          <BadgeDollarSign className={cn('mt-0.5 h-4 w-4 shrink-0', !free ? 'text-brand' : 'text-muted-foreground')} />
          <span>
            <span className="block text-sm font-medium">Paid</span>
            <span className="mt-1 block text-xs leading-5 text-muted-foreground">One or more rates, each billed per call to the operations you link to it.</span>
          </span>
        </button>
      </div>

      {rows.map((row, index) => (
        <div key={index} className="rounded-xl border border-border bg-card/40 p-5">
          <div className="grid gap-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Field id={`rate-label-${index}`} label="Label (English)">
              <Input id={`rate-label-${index}`} value={row.label} disabled={readOnly} onChange={(event) => setRow(index, { label: event.target.value })} placeholder="Directory lookups" />
            </Field>
            <Field id={`rate-id-${index}`} label="Rate id" hint={row.isNew ? 'Derived from the label.' : 'Fixed once saved; usage events reference it.'}>
              <Input id={`rate-id-${index}`} value={row.id} disabled className="font-mono text-xs" />
            </Field>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field id={`rate-price-${index}`} label="Price (USD)">
              <Input
                id={`rate-price-${index}`}
                type="number"
                min={0}
                step="0.0001"
                value={Number.isFinite(row.providerRate) ? row.providerRate : 0}
                disabled={readOnly || row.billingModel === 'included'}
                onChange={(event) => setRow(index, { providerRate: Number(event.target.value) })}
                className="font-mono"
              />
            </Field>
            <Field id={`rate-units-${index}`} label="Covers this many calls" hint="1,000 for “per 1,000 calls”.">
              <Input
                id={`rate-units-${index}`}
                type="number"
                min={1}
                step={1}
                value={row.unitsPerPrice}
                disabled={readOnly || row.billingModel === 'included'}
                onChange={(event) => setRow(index, { unitsPerPrice: Math.max(1, Math.floor(Number(event.target.value) || 1)) })}
                className="font-mono"
              />
            </Field>
            <Field label="Billing">
              <Select value={row.billingModel} disabled={readOnly} onValueChange={(value) => setRow(index, { billingModel: value as RateRow['billingModel'] })}>
                <SelectTrigger aria-label="Billing model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_MODELS.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Metered on">
              <Select value={row.interval} disabled={readOnly} onValueChange={(value) => setRow(index, { interval: value as RateRow['interval'] })}>
                <SelectTrigger aria-label="Metered on">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVALS.map((interval) => (
                    <SelectItem key={interval.id} value={interval.id}>
                      {interval.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field id={`rate-unit-${index}`} label="Unit, as customers read it">
              <Input id={`rate-unit-${index}`} value={row.unit} disabled={readOnly} onChange={(event) => setRow(index, { unit: event.target.value })} placeholder="per 1,000 calls" />
            </Field>
            <Field id={`rate-free-${index}`} label="Free each month" optional hint="Calls per organization per calendar month (UTC) that cost nothing.">
              <Input
                id={`rate-free-${index}`}
                type="number"
                min={0}
                step={1}
                value={row.freeTier ?? ''}
                disabled={readOnly}
                onChange={(event) => setRow(index, { freeTier: event.target.value ? Math.max(0, Math.floor(Number(event.target.value))) : undefined })}
                placeholder="10000"
                className="font-mono"
              />
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-4">
            <div className="flex flex-wrap gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Card reads</div>
                <div className="font-mono text-sm">{formatRate(toRate(row))}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Each call costs</div>
                <div className="font-mono text-sm">{perCallCost(toRate(row))} + markup</div>
              </div>
            </div>
            {readOnly ? null : (
              <Button variant="ghost" size="icon" aria-label="Remove rate" onClick={() => removeRow(index)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
      {!free && !readOnly && rows.length < 20 ? (
        <Button variant="outline" onClick={addRow}>
          <Plus className="mr-2 h-4 w-4" />
          Add another rate
        </Button>
      ) : null}

      {!free ? (
        <div className="overflow-hidden rounded-xl border border-border">
          <div className="bg-card/40 px-5 py-3">
            <div className="text-sm font-semibold">What each operation bills against</div>
            <div className="text-xs text-muted-foreground">One successful call is one unit of its rate. Operations left on Free cost nothing.</div>
          </div>
          {operations.length === 0 ? (
            <div className="border-t border-border px-5 py-6 text-sm text-muted-foreground">Enable operations on the API contract step first.</div>
          ) : (
            operations.map((operation) => (
              <div key={operation.operationId} className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
                <div className="min-w-0">
                  <div className="font-mono text-xs">
                    {operation.method} {operation.path}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{operation.summary || operation.operationId}</div>
                </div>
                <div className="w-56">
                  <Select
                    value={links[operation.operationId] ?? FREE}
                    disabled={readOnly}
                    onValueChange={(value) => setLinks((current) => ({ ...current, [operation.operationId]: value }))}
                  >
                    <SelectTrigger aria-label={`Rate for ${operation.operationId}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={FREE}>Free</SelectItem>
                      {rows.map((row) => (
                        <SelectItem key={row.id} value={row.id}>
                          {row.label || row.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}
    </StepFrame>
  );
}
